export const formatBytes = (bytes: number, decimals: number = 2): string => {
	if (!bytes) {
		return "0 Bytes";
	}

	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	const value = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

	return `${value} ${sizes[i]}`;
};

export const isImage = (contentType?: string): boolean => {
	return !!contentType?.startsWith("image");
};

export const fromBytesToImageSrc = (type: string, bytes: string): string =>
	`data:${type};base64,${bytes}`;

export const fileToImageSrc = (file?: File): Promise<string> =>
	new Promise((resolve: any, reject: any) => {
		if (!FileReader || !file) {
			reject("FileReader doesn't exists or file is null");

			return;
		}

		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});

export const fileToBase64 = async (file?: File): Promise<string> => {
	if (!file) {
		return "";
	}
	const imageSrc = await fileToImageSrc(file);

	return imageSrc.slice(fromBytesToImageSrc(file.type, "").length);
};

export const splitImageSrc = (src?: string): [string, string] => {
	if (!src) {
		return ["", ""];
	}

	const matches = [...src.matchAll(/data:(.+?);base64,(.*)/g)];

	if (![...matches].length) {
		return ["", ""];
	}

	const match = matches[0]!;

	return [match[1] || "", match[2] || ""];
};

export const base64ToFile = (
	src: string,
	contentType: string,
	fileName: string
): File => {
	const u8arr = Uint8Array.from(
		Array.from(atob(src)).map((x: string) => x.charCodeAt(0))
	);

	return new File([u8arr], fileName, { type: contentType });
};

export const getFileExtensionFromName = (filename: string) => {
	return filename.split(".").pop() || "";
};

const PastedImageMimeToExtension: Record<string, string> = {
	"image/png": "png",
	"image/jpeg": "jpg",
	"image/gif": "gif",
	"image/webp": "webp",
	"image/bmp": "bmp",
	"image/svg+xml": "svg",
};

const isGenericClipboardName = (name: string): boolean => {
	return !name || name.toLowerCase() === "image.png";
};

const buildPastedFileName = (file: File, index: number): string => {
	const extension =
		PastedImageMimeToExtension[file.type] ??
		file.type.split("/")[1]?.split(";")[0] ??
		"bin";
	const now = new Date();
	const pad = (value: number) => value.toString().padStart(2, "0");
	const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
		now.getDate()
	)}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
	const unique = crypto.randomUUID().slice(0, 8);
	const prefix = file.type.startsWith("image/") ? "pasted-image" : "pasted-file";

	return `${prefix}-${timestamp}-${index}-${unique}.${extension}`;
};

export const getFilesFromClipboard = (
	clipboard: DataTransfer | null
): File[] => {
	if (!clipboard) {
		return [];
	}

	const collected: File[] = [];

	if (clipboard.items?.length) {
		Array.from(clipboard.items).forEach((item: DataTransferItem) => {
			if (item.kind === "file") {
				const file = item.getAsFile();
				if (file) {
					collected.push(file);
				}
			}
		});
	}

	if (collected.length === 0 && clipboard.files?.length) {
		collected.push(...Array.from(clipboard.files));
	}

	return collected.map((file: File, index: number) => {
		if (!isGenericClipboardName(file.name)) {
			return file;
		}

		return new File([file], buildPastedFileName(file, index), {
			type: file.type,
		});
	});
};

type PasteEventLike = {
	clipboardData: DataTransfer | null;
	preventDefault: () => void;
};

export const handleAttachmentPaste = (
	event: PasteEventLike,
	enabled: boolean,
	onFiles: (files: File[]) => void
): void => {
	if (!enabled) {
		return;
	}

	const pastedFiles = getFilesFromClipboard(event.clipboardData);

	if (pastedFiles.length === 0) {
		return;
	}

	event.preventDefault();
	onFiles(pastedFiles);
};
